import { useContext, useEffect, useRef, useMemo } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import { useRequestData } from 'wsm-common-data';
import { IncentivesContext, FiltersContext, UIContext } from '../contexts';
import { filterVehicles } from '../utils/filterVehicles';
import { WIDGET_STATES } from '../utils/trackingConstants';
import { toSlug } from '../utils/toSlug';

export const FACET_KEYS = ['offerTypes', 'year', 'bodyStyle', 'make', 'model'];

/**
 * Subset of FACET_KEYS that participate in URL deep linking.
 *
 * Rally F243497, story 9-1: only `offerTypes` is serialized into the URL
 * query string. Year, Body Style, Make, and Model remain in-session filters
 * (driven by FiltersContext, displayed via ActiveFilterChips) but never
 * round-trip through the URL. This keeps campaign URLs clean and predictable
 * (e.g. `?offerTypes=lease`) and treats non-`offerTypes` URL params as
 * silently ignored legacy input.
 *
 * FACET_KEYS remains the source of truth for "every facet the UI knows
 * about" — consumers that need the full set (chips, slug map construction)
 * continue to import FACET_KEYS.
 */
export const URL_FACET_KEYS = ['offerTypes'];

export const buildSlugMap = (byVehicle) => {
	const map = {};
	FACET_KEYS.forEach((key) => {
		map[key] = {};
	});
	Object.values(byVehicle || {}).forEach((vehicle) => {
		FACET_KEYS.forEach((key) => {
			const rawValues =
				key === 'offerTypes' ? vehicle[key] || [] : [vehicle[key]];
			rawValues.forEach((rawVal) => {
				if (!rawVal) return;
				const slug = toSlug(rawVal);
				map[key][slug] = rawVal; // slug → display
				map[key][rawVal] = slug; // display → slug (for write path)
			});
		});
	});
	return map;
};

const useDeepLinking = () => {
	const { incentives } = useContext(IncentivesContext);
	const { activeFilters, hydrateFromUrl } = useContext(FiltersContext);
	const { activeView } = useContext(UIContext);

	const { widgetName, windowId } = useRequestData();

	const slugMap = useMemo(
		() => buildSlugMap(incentives?.byVehicle || {}),
		[incentives]
	);

	const isMounted = useRef(false);

	// URL Hydration — runs once on mount
	useEffect(() => {
		if (!isBrowser) return;
		const params = new URLSearchParams(window.location.search);
		if (params.get('gvId')) return; // detail view boot — skip facet hydration

		const validFilters = {};
		const staleFilters = {};
		let hasAnyParams = false;

		URL_FACET_KEYS.forEach((key) => {
			const raw = params.get(key);
			if (!raw) return;
			hasAnyParams = true;
			const slugs = raw.split(',').filter(Boolean);
			slugs.forEach((slug) => {
				if (slugMap[key][slug] !== undefined) {
					validFilters[key] = [...(validFilters[key] || []), slug];
				} else {
					staleFilters[key] = [...(staleFilters[key] || []), slug];
				}
			});
		});

		if (!hasAnyParams) return;

		const hasValid = Object.keys(validFilters).length > 0;
		const hasStale = Object.keys(staleFilters).length > 0;
		let alertState = null;
		if (hasStale) {
			alertState = hasValid ? 'partial' : 'total';
		}
		hydrateFromUrl(validFilters, staleFilters, alertState);

		if (hasValid) {
			const resultCount = filterVehicles(
				incentives?.byVehicle,
				validFilters
			).length;
			trackEvent(widgetName, windowId, {
				element: 'Facet Filter',
				action: 'hydrated',
				result: `${resultCount} results`,
				nonInteractive: true,
				widgetState: WIDGET_STATES.DEFAULT
			});
		}

		if (alertState) {
			trackEvent(widgetName, windowId, {
				element: 'Stale URL Alert',
				action: 'displayed',
				result: alertState,
				nonInteractive: true,
				widgetState: WIDGET_STATES.DEFAULT
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// URL Write — on every activeFilters change after mount.
	// isMounted guards the first call: skip it so hydration state has a chance
	// to propagate before the first URL write (prevents a transient URL clear
	// when the page loads with existing facet params).
	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}
		if (!isBrowser || activeView === 'detail') return;

		const params = new URLSearchParams(window.location.search);

		// Remove URL-scoped facet params first.
		// Non-URL facets (year/bodyStyle/make/model) are never touched — their
		// selection is kept out of the URL entirely (Rally F243497, story 9-1).
		URL_FACET_KEYS.forEach((key) => params.delete(key));

		// Re-add non-empty URL-scoped facets as slugs.
		// activeFilters values are already lowercase slugs (from getCascadingFacetOptions),
		// so toSlug() is idempotent here. The display→slug direction of slugMap is unused
		// on the write path because activeFilters never stores display-name strings.
		URL_FACET_KEYS.forEach((key) => {
			const values = activeFilters[key] || [];
			if (values.length === 0) return;
			const slugs = values.map((v) => toSlug(v));
			params.set(key, slugs.join(','));
		});

		const search = params.toString();
		const newUrl = window.location.pathname + (search ? `?${search}` : '');
		window.history.replaceState(null, '', newUrl);
	}, [activeFilters, activeView]);

	return {};
};

export default useDeepLinking;
